import type { BlockPerimeters } from "building-types/blocks/blockTypes";
import type {
  DocEntityHandle,
  RoofEdgeHandle,
  RoofHandle,
  RoofSideHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { DormerParentRoofSettings, RoofDormer } from "building-types/roofs/roofGenerationTypes";
import type { DormerInputSettings } from "building-types/roofs/roofInputSettings";
import type { RoofDormerPlacement, VirtualRoofBlock } from "building-types/roofs/roofSystemTypes";
import { isIndexedRoofPeakDirection } from "building-types/roofs/roofSystemTypes";
import type { GeneratedRoofVolumeLegacy } from "building-types/roofVolumes/roofVolumeTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { DormerSettings } from "building-types/settings/dormerSettings";
import type { MergeSettingsForEntityParams } from "building-types/settings/mergeSettingsForEntityParams";
import { BuildingSide, RoofSide } from "building-types/settings/roofTypes";
import { EaveFasciaHeightDatum, GableRoofType } from "building-types/settings/settingsTypesEnum";
import type {
  GeneratedWindowPlacement,
  WindowDescriptor,
  WindowShutters,
  WindowUnit,
} from "building-types/windows/windowTypes";
import type { RWTable, Table } from "common/data/table";
import uid from "common/utils/uid";
import { isVirtualRoofBlock } from "doc/roofSystemTools";
import type { Face } from "geom/brep";
import { polyLine2Copy, polyLine2Reverse, polyLine2Shift } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3EdgeLength } from "geom/polyLine3";
import { rangeNonInclusiveByMinStep } from "geom/range";
import { convertDormerDistParam, isValidRoofTypeForEdgeOverrides } from "../multiGableRoofTools";
import { getDormerWidth, roofDormerOnFace } from "./dormers";

const MIN_DORMER_SPACING = 12;

export function resolveDormerSpacing(
  dormerSettings: DormerSettings,
  dormer: RoofDormerPlacement,
  windowCatalog: Table<WindowDescriptor>,
): number {
  const defaultSpacing = getDormerWidth(dormerSettings, dormer, windowCatalog) + 90;
  const { spacing } = dormerSettings;

  if (spacing === undefined || !Number.isFinite(spacing) || spacing < MIN_DORMER_SPACING) {
    return defaultSpacing;
  }

  return spacing;
}

export interface AddDormerOptions extends MergeSettingsForEntityParams {
  roofIndex: number;
  roofSystemId: string;
  level: Level;
  clipContext: MultiGableRoofClipContext;
  subRoof: GeneratedRoof;
  side: RoofSide;
  rail3: PolyLine3;
  face: Face;
  faceSide: BuildingSide;
  mirror: boolean;
  dormer: RoofDormerPlacement;
  dormerInputSettings: DormerInputSettings;
  dormerSettings: DormerSettings;
  roofParentSettings: DormerParentRoofSettings;
  dormers: RWTable<RoofDormer>;
  windowCatalog: Table<WindowDescriptor>;
  windows: RWTable<GeneratedWindowPlacement>;
  windowUnits: Table<WindowUnit>;
  windowShutters: Table<WindowShutters>;
  addRoofWallFaces: boolean;
  generatedDormerRoofVolumes: RWTable<GeneratedRoofVolumeLegacy>;
  generatedDormerRoofs: RWTable<GeneratedRoof>;
  issues: BuildingGenerationIssue[];
  entityHandleForSelectingReportingUnit?: DocEntityHandle;
}

export async function addDormers({
  dormerSettings,
  roofIndex,
  roofSystemId,
  level,
  clipContext,
  subRoof,
  side,
  rail3,
  face,
  faceSide,
  mirror,
  dormer,
  dormerInputSettings,
  roofParentSettings,
  dormers,
  windowCatalog,
  windows,
  windowUnits,
  windowShutters,
  addRoofWallFaces,
  issues,
  generatedDormerRoofVolumes,
  generatedDormerRoofs,
  entitySettings,
  activeSettingsSets,
  optionChoices,
  settings,
  entityHandleForSelectingReportingUnit,
}: AddDormerOptions): Promise<number> {
  if (!subRoof) {
    return roofIndex;
  }

  const defaultDormerV = 40;
  const { start, end, normalizedPositions = false, verticalPosition = defaultDormerV } = dormerSettings;
  const spacing = resolveDormerSpacing(dormerSettings, dormer, windowCatalog);

  const flipDistances = (faceSide === BuildingSide.FRONT || faceSide === BuildingSide.BACK) !== mirror;

  const {
    roofVolume: { rectangularOverhangs },
  } = subRoof;

  const railLength =
    polyLine3EdgeLength(rail3, 0) - rectangularOverhangs.baseLeftOverhang - rectangularOverhangs.baseRightOverhang;

  const dormerUs: number[] = [];

  // this used to check for `undefined` which meant that a null value could pass through
  if (dormerSettings.horizontalPositions) {
    dormerUs.push(
      ...dormerSettings.horizontalPositions.map((u) => {
        return convertDormerDistParam(u, railLength, 0.0, normalizedPositions);
      }),
    );
  }

  if (dormerUs.length === 0) {
    const defaultEnd = normalizedPositions ? 1.0 : railLength;
    const dormerStart = convertDormerDistParam(start, railLength, 0.0, normalizedPositions);
    const dormerEnd = convertDormerDistParam(end, railLength, defaultEnd, normalizedPositions);

    if (dormerEnd >= dormerStart) {
      dormerUs.push(...rangeNonInclusiveByMinStep(dormerStart, dormerEnd, spacing));
    }
  }

  if (dormerUs.length > 0) {
    for (let j = 0, lj = dormerUs.length; j < lj; j++) {
      const t = flipDistances ? dormerUs[j] : railLength - dormerUs[j];
      const dormerInstanceId = uid();
      const result = await roofDormerOnFace({
        roofIndex: roofIndex++,
        roofSystemId,
        level,
        clipContext,
        subRoof,
        side,
        face,
        u: t,
        v: verticalPosition,
        dormerInputSettings,
        roofParentSettings,
        dormer,
        windowCatalog,
        windows,
        windowUnits,
        windowShutters,
        addRoofWallFaces,
        issues,
        generatedDormerRoofVolumes,
        generatedDormerRoofs,
        mirror,
        addWindowShutters: true,
        entitySettings,
        activeSettingsSets,
        optionChoices,
        settings,
        entityHandleForSelectingReportingUnit,
      });
      if (!result) {
        continue;
      }
      dormers[dormerInstanceId] = result;
    }
  }

  return roofIndex;
}

export function filterUpperFloorPerimeters(
  perimeters: BlockPerimeters,
  filter: (blockId: string) => boolean,
): BlockPerimeters {
  const ret: RWTable<PolyLine2[]> = {};
  for (const blockId in perimeters) {
    if (filter && filter(blockId)) {
      ret[blockId] = perimeters[blockId];
    }
  }

  return ret;
}

interface GetBaseHeightFromHostRoofOptions {
  eaveFasciaHeightDatum: EaveFasciaHeightDatum;
  hostSubRoof: GeneratedRoofVolumeLegacy;
  currBaseHeight: number;
  heelHeightOffset: number;
}

export function getBaseHeightFromHostRoof({
  eaveFasciaHeightDatum,
  hostSubRoof,
  currBaseHeight,
  heelHeightOffset,
}: GetBaseHeightFromHostRoofOptions): number {
  if (!hostSubRoof) {
    return currBaseHeight;
  }

  const { baseHeight: hostBaseHeight, verticalRails: hostVerticalRails } = hostSubRoof;

  const {
    truncatedStartHeight: hostTruncatedStartHeight = hostBaseHeight,
    truncatedEndHeight: hostTruncatedEndHeight = hostBaseHeight,
  } = hostVerticalRails;

  switch (eaveFasciaHeightDatum) {
    case EaveFasciaHeightDatum.HOST_ROOF:
      return hostBaseHeight + heelHeightOffset;
    case EaveFasciaHeightDatum.HOST_ROOF_START_TRUNCATION:
      return hostBaseHeight + hostTruncatedStartHeight + heelHeightOffset;
    case EaveFasciaHeightDatum.HOST_ROOF_END_TRUNCATION:
      return hostBaseHeight + hostTruncatedEndHeight + heelHeightOffset;
    default:
      return currBaseHeight;
  }
}

export function getRoofSideHandles(roofHandle: RoofHandle): RoofSideHandle[] {
  const roofSides: RoofSide[] = [RoofSide.START, RoofSide.RIGHT, RoofSide.END, RoofSide.LEFT];
  const roofSideHandles: RoofSideHandle[] = [];

  for (const side of roofSides) {
    roofSideHandles.push({
      parent: roofHandle,
      type: "ROOF_SIDE",
      side,
    });
  }

  return roofSideHandles;
}

export function getRoofEdgeHandles(roofHandle: RoofHandle, virtualRoofBlock: VirtualRoofBlock): RoofEdgeHandle[] {
  const { edgeIds = [] } = virtualRoofBlock;

  return edgeIds.map((edgeId) => ({
    parent: roofHandle,
    shapeId: "outline",
    edgeId: edgeIds.indexOf(edgeId),
    type: "EDGE",
  }));
}

export function getRoofEdgeLabel(handle: RoofEdgeHandle, virtualRoofBlock: VirtualRoofBlock): string {
  const { edgeIds } = virtualRoofBlock;

  // use the edge name if it exists, otherwise use the edge index
  return edgeIds[handle.edgeId] === "" ? `#${handle.edgeId}` : `${edgeIds[handle.edgeId]}`;
}

export function isValidRoofForEdgeOverrides(generatedRoof?: GeneratedRoof): boolean {
  if (!generatedRoof) {
    return false;
  }

  const { roofSettings, roofVolume } = generatedRoof;
  const { blocks } = roofVolume;

  return isValidRoofTypeForEdgeOverrides(roofSettings, blocks);
}

export function getRoofSidesOutline(subRoof: GeneratedRoof | undefined, mirror: boolean): PolyLine2 | null {
  if (!subRoof) {
    return null;
  }

  const { rails2D, roofSettings, roofType } = subRoof;
  const { peakDirection } = roofSettings;
  const { startRail, endRail, leftRail, rightRail, peakRail } = rails2D;

  let outline = polyLine2New([
    startRail[0],
    startRail[1],
    rightRail[0],
    rightRail[1],
    endRail[2],
    endRail[3],
    leftRail[2],
    leftRail[3],
    startRail[0],
    startRail[1],
  ]);

  if (roofType === GableRoofType.LEFT_SIDE_SHED_ROOF) {
    outline = polyLine2New([
      leftRail[0],
      leftRail[1],
      peakRail[0],
      peakRail[1],
      peakRail[2],
      peakRail[3],
      leftRail[2],
      leftRail[3],
      leftRail[0],
      leftRail[1],
    ]);
  } else if (roofType === GableRoofType.RIGHT_SIDE_SHED_ROOF) {
    outline = polyLine2New([
      peakRail[0],
      peakRail[1],
      rightRail[0],
      rightRail[1],
      rightRail[2],
      rightRail[3],
      peakRail[2],
      peakRail[3],
      peakRail[0],
      peakRail[1],
    ]);
  }

  if (mirror) {
    const shift = peakDirection !== undefined && isIndexedRoofPeakDirection[peakDirection] ? -1 : 1;
    polyLine2Shift(polyLine2Reverse(outline), shift);
  }

  return outline;
}

export function getRoofEdgesOutline(subRoof: GeneratedRoof, mirror: boolean): PolyLine2 | null {
  const {
    roofVolume: { blocks },
  } = subRoof;

  if (!isVirtualRoofBlock(blocks)) {
    return null;
  }

  const perimeter = polyLine2Copy(blocks.perimeter);

  if (mirror) {
    polyLine2Shift(polyLine2Reverse(perimeter), 1);
  }

  return perimeter;
}
